import { Plus, X } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';

import { cn } from '@/lib/utils';

interface Props {
  label: string;
  inputAriaLabel?: string;
  value: string[] | null | undefined;
  onChange: (value: string[]) => void;
  suggestions?: string[];
  disabled?: boolean;
}

export function TechniquesCombobox({
  label,
  inputAriaLabel,
  value,
  onChange,
  suggestions = [],
  disabled = false,
}: Props) {
  const tags = value ?? [];
  const [draft, setDraft] = useState('');

  const add = (raw: string): void => {
    const trimmed = raw.trim();
    if (trimmed === '' || tags.includes(trimmed)) return;
    onChange([...tags, trimmed]);
    setDraft('');
  };

  const remove = (tag: string): void => {
    onChange(tags.filter((t) => t !== tag));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && draft !== '') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && draft === '' && tags.length > 0) {
      e.preventDefault();
      const last = tags.at(-1);
      if (last !== undefined) remove(last);
    }
  };

  const filteredSuggestions = suggestions
    .filter((s) => !tags.includes(s))
    .filter((s) => draft === '' || s.toLowerCase().includes(draft.toLowerCase()))
    .slice(0, 6);

  return (
    <div className="flex flex-col gap-2.5">
      {label !== '' ? (
        <span className="text-accent-ink text-[13px] font-medium">{label}</span>
      ) : null}
      <div
        className={cn(
          'border-accent-ink/15 focus-within:border-primary-300 focus-within:ring-primary-100 flex flex-wrap items-center gap-1.5 rounded-lg border bg-white px-2 py-1.5 transition-colors focus-within:ring-2',
          disabled && 'opacity-60',
        )}
      >
        {tags.map((tag) => (
          <span
            key={tag}
            className="bg-primary-50 text-primary-700 ring-primary-200/60 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1"
          >
            {tag}
            {!disabled ? (
              <button
                type="button"
                onClick={() => {
                  remove(tag);
                }}
                aria-label={`Remove ${tag}`}
                className="text-primary-700/60 hover:text-primary-700 -mr-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full transition-colors hover:bg-white/70"
              >
                <X className="h-2.5 w-2.5" strokeWidth={2} />
              </button>
            ) : null}
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          onKeyDown={onKeyDown}
          disabled={disabled}
          placeholder={tags.length === 0 ? 'Type and press ↵' : ''}
          className="text-accent-ink min-w-20 flex-1 border-none bg-transparent px-1 py-0.5 text-[13px] outline-none placeholder:text-neutral-400 focus:outline-none"
          aria-label={inputAriaLabel ?? (label !== '' ? label : undefined)}
        />
      </div>
      {filteredSuggestions.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {filteredSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              disabled={disabled}
              onClick={() => {
                add(s);
              }}
              className="bg-mint-paper/45 ring-primary-200/40 hover:bg-mint-paper hover:ring-primary-300/60 text-accent-ink/80 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] ring-1 transition-all"
            >
              <Plus className="h-2.5 w-2.5 opacity-60" strokeWidth={2} />
              {s}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
